// Legacy 'helpdeskTicketDetail' component. This could be removed because the new /helpdeskTicketDetail exists, but this is used on insuranceOverview // cc @Bjorn

import React, { useState, useEffect, useRef } from "react";
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  Button,
  Spinner,
  Tooltip,
  Table,
  TableHeader,
  TableColumn,
  TableBody,
  TableRow,
  TableCell,
  Chip,
  Input,
  Textarea,
} from "@heroui/react";
import {
  X,
  Envelope,
  Paperclip,
  Download,
  Plus,
  Tag,
  ArrowRight,
  ArrowLeft,
  Eye,
  Image,
  ArrowClockwise,
  ChatCircle,
  User,
  Clock,
  UserGear,
} from "@phosphor-icons/react";
import { API_URL } from "constants/index";
import { fetchWithAuth } from "shared/api/fetch-with-auth";
import { useSession, useUser } from "@clerk/clerk-react";
import { toast } from "react-toastify";
import { QuickReplyModal } from "./quickReplyModal";
import { TicketInternalStatus } from "@/shared/types/helpdesk";

interface EmailAttachment {
  id: string;
  name: string;
  size: number;
  contentType: string;
  sharePointFileId?: string;
  sharePointFileUrl?: string;
}

interface Email {
  id: number;
  messageId: string;
  subject: string;
  body: string;
  from: string;
  receivedDateTime: string;
  direction: "sent" | "received";
  isRead: boolean;
  hasAttachments?: boolean;
  attachments?: EmailAttachment[];
  metadata?: {
    to?: string;
    supplierCommunication?: boolean;
  };
}

interface ActionNote {
  id: number;
  message: string;
  userId: string;
  createdAt: string;
  remindMeAt?: string;
}

interface EmailModalProps {
  isOpen: boolean;
  onClose: () => void;
  ticket: any;
  onEmailsRead?: () => void;
  onTicketUpdated?: () => void;
  communicationMode?: "tenant" | "supplier";
  isInsurance?: boolean;
  insuranceIssueId?: number;
  insuranceContacts?: Array<{
    id: number;
    firstName?: string;
    lastName?: string;
    email?: string;
  }>;
  onUpdateInsuranceContacts?: (
    contacts: Array<{
      id: number;
      firstName?: string;
      lastName?: string;
      email?: string;
    }>,
  ) => void;
}

export const EmailModal: React.FC<EmailModalProps> = ({
  isOpen,
  onClose,
  ticket,
  onEmailsRead,
  onTicketUpdated,
  communicationMode = "tenant",
  isInsurance = false,
  insuranceIssueId,
  insuranceContacts = [],
  onUpdateInsuranceContacts,
}) => {
  console.log("📩 EmailModal component rendered with props:", {
    isOpen,
    ticketId: ticket?.id,
    communicationMode,
    isInsurance,
  });
  const [emails, setEmails] = useState<Email[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingAttachments, setIsLoadingAttachments] = useState<Set<string>>(
    new Set(),
  );
  const [showQuickReply, setShowQuickReply] = useState(false);
  const [replyToSubject, setReplyToSubject] = useState("");
  const [contactSearch, setContactSearch] = useState("");
  const [contactOptions, setContactOptions] = useState<any[]>([]);
  const [localContacts, setLocalContacts] = useState(insuranceContacts);
  const [isUpdatingContacts, setIsUpdatingContacts] = useState(false);
  const [selectedEmail, setSelectedEmail] = useState<Email | null>(null);
  const [processedEmailBodies, setProcessedEmailBodies] = useState<
    Map<string, string>
  >(new Map());
  const [loadingInlineImages, setLoadingInlineImages] = useState<Set<string>>(
    new Set(),
  );
  const { session } = useSession();
  const { user } = useUser();
  const emailsFetchedRef = useRef(false);
  const selectedInitializedRef = useRef(false);
  const lastCommunicationModeRef = useRef(communicationMode);
  const disableHoverOverlays = Boolean(selectedEmail);

  // Action Notes state
  const [actionNotes, setActionNotes] = useState<ActionNote[]>([]);
  const [isLoadingNotes, setIsLoadingNotes] = useState(false);
  const [showAddNote, setShowAddNote] = useState(false);
  const [newMessage, setNewMessage] = useState("");
  const [newRemindMeAt, setNewRemindMeAt] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Communication mode and tab state
  const [activeTab, setActiveTab] = useState<"emails" | "notes">("emails");

  const isRecentIncoming = (email: Email) => {
    const emailDate = new Date(email.receivedDateTime);
    const now = new Date();
    const diffHours = (now.getTime() - emailDate.getTime()) / (1000 * 60 * 60);
    return email.direction === "received" && diffHours <= 24;
  };

  useEffect(() => {
    if (!isOpen) {
      emailsFetchedRef.current = false;
      selectedInitializedRef.current = false;
      lastCommunicationModeRef.current = communicationMode;
      return;
    }
    if (isOpen) {
      // Always start on the emails tab when modal opens
      setActiveTab("emails");

      const modeChanged =
        lastCommunicationModeRef.current !== communicationMode;
      console.log("EmailModal opened/updated:", {
        ticketId: ticket?.id,
        communicationMode,
        lastMode: lastCommunicationModeRef.current,
        modeChanged,
        emailsFetched: emailsFetchedRef.current,
      });

      if (!emailsFetchedRef.current || modeChanged) {
        emailsFetchedRef.current = true;
        lastCommunicationModeRef.current = communicationMode;
        fetchEmails();
      }
    }
  }, [isOpen, communicationMode]);

  useEffect(() => {
    // Avoid infinite loops when parent passes a new array instance each render
    setLocalContacts((prev) => {
      if (prev === insuranceContacts) return prev;
      if (!Array.isArray(insuranceContacts)) return prev;
      if (prev.length === insuranceContacts.length) {
        let same = true;
        for (let i = 0; i < prev.length; i++) {
          const a = prev[i];
          const b = insuranceContacts[i];
          if (
            !b ||
            a.id !== b.id ||
            a.email !== b.email ||
            a.firstName !== b.firstName ||
            a.lastName !== b.lastName
          ) {
            same = false;
            break;
          }
        }
        if (same) return prev;
      }
      return insuranceContacts;
    });
  }, [insuranceContacts]);

  // Process email bodies for inline images when emails are loaded
  useEffect(() => {
    if (emails.length > 0) {
      // Process all emails that have bodies and might contain inline images
      emails.forEach((email) => {
        if (email.body && email.body.includes("cid:")) {
          processEmailBody(email);
        }
      });
    }
  }, [emails]);

  // Cleanup blob URLs when component unmounts
  useEffect(() => {
    return () => {
      // Revoke all blob URLs to prevent memory leaks
      inlineImageCacheRef.current.forEach((url) => {
        if (url.startsWith("blob:")) {
          URL.revokeObjectURL(url);
        }
      });
    };
  }, []);

  // Fetch action notes when modal opens
  useEffect(() => {
    if (isOpen && ticket?.id && !isInsurance) {
      fetchActionNotes();
      markTicketAsAnalyzed();
    }
  }, [isOpen, ticket?.id, isInsurance]);

  const fetchEmails = async () => {
    setIsLoading(true);
    try {
      const endpoint = isInsurance
        ? `${API_URL}/insurance/file/${insuranceIssueId}/emails`
        : `${API_URL}/helpdesk/ticket/${ticket.id}/emails`;

      const response = await fetchWithAuth(
        endpoint,
        { method: "GET" },
        session,
      );

      if (!response.ok) {
        throw new Error("Failed to fetch emails");
      }

      const data = await response.json();

      // Filter emails based on communication mode
      let filteredEmails = data;
      if (!isInsurance && communicationMode) {
        console.log(
          `Filtering emails for mode: ${communicationMode}, total: ${data.length}`,
        );
        filteredEmails = data.filter((email: Email) => {
          if (communicationMode === "supplier") {
            // Show supplier emails (from/to repairs@yally.be or with HS{id} in subject)
            const isSupplier =
              email.from?.includes("repairs@yally.be") ||
              email.metadata?.to?.includes("repairs@yally.be") ||
              email.subject?.includes(`HS${ticket.id}`) ||
              email.metadata?.supplierCommunication === true;
            if (isSupplier) {
              console.log("Supplier email found:", email.subject);
            }
            return isSupplier;
          } else {
            // Show tenant emails (from/to helpdesk@yally.be or with H{id} in subject, not supplier emails)
            const isSupplierEmail =
              email.from?.includes("repairs@yally.be") ||
              email.metadata?.to?.includes("repairs@yally.be") ||
              email.subject?.includes(`HS${ticket.id}`) ||
              email.metadata?.supplierCommunication === true;
            return !isSupplierEmail;
          }
        });
        console.log(`Filtered to ${filteredEmails.length} emails`);
      }

      setEmails(filteredEmails);

      // Auto-select the most recent incoming email for immediate viewing
      const recentIncoming = filteredEmails
        .filter((email: Email) => email.direction === "received")
        .sort(
          (a: Email, b: Email) =>
            new Date(b.receivedDateTime).getTime() -
            new Date(a.receivedDateTime).getTime(),
        )[0];

      if (recentIncoming && !selectedInitializedRef.current) {
        setSelectedEmail(recentIncoming);
        selectedInitializedRef.current = true;
      }

      // If there are no emails or no recent emails, default to notes tab for better UX
      if (!isInsurance && filteredEmails.length === 0) {
        setActiveTab("notes");
      }

      // Mark emails as read if there are unread ones
      const hasUnreadEmails = data.some(
        (email: Email) => !email.isRead && email.direction === "received",
      );
      if (hasUnreadEmails) {
        try {
          const markReadEndpoint = isInsurance
            ? `${API_URL}/insurance/file/${insuranceIssueId}/emails/mark-read`
            : `${API_URL}/helpdesk/ticket/${ticket.id}/emails/mark-read`;

          await fetchWithAuth(markReadEndpoint, { method: "PUT" }, session);
          onEmailsRead?.();
        } catch (error) {
          console.error("Error marking emails as read:", error);
        }
      }
    } catch (error) {
      console.error("Error fetching emails:", error);
      toast.error("Fout bij laden van emails");
    } finally {
      setIsLoading(false);
    }
  };

  const fetchEmailAttachments = async (email: Email) => {
    if (!email.hasAttachments) return;
    if (email.attachments && email.attachments.length > 0) return;

    try {
      setIsLoadingAttachments((prev) => new Set(prev).add(email.messageId));

      // Determine email type based on context
      let emailType = "acquisitions";
      if (isInsurance) {
        emailType = "insurance";
      } else if (ticket && !isInsurance) {
        // Helpdesk context - check if this is actually a helpdesk ticket
        emailType = "helpdesk";
      }

      const response = await fetchWithAuth(
        `${API_URL}/email/message-details/${emailType}/${email.messageId}`,
        { method: "GET" },
        session,
      );

      if (!response.ok) {
        throw new Error("Failed to fetch email attachments");
      }

      const data = await response.json();

      setEmails((prevEmails) =>
        prevEmails.map((e) =>
          e.messageId === email.messageId
            ? { ...e, attachments: data.attachments || [] }
            : e,
        ),
      );
    } catch (error) {
      console.error("Error fetching email attachments:", error);
      toast.error("Fout bij laden van bijlagen");
    } finally {
      setIsLoadingAttachments((prev) => {
        const newSet = new Set(prev);
        newSet.delete(email.messageId);
        return newSet;
      });
    }
  };

  const handleDownloadAttachment = async (
    attachment: EmailAttachment,
    email: Email,
  ) => {
    try {
      let response: Response;

      // For helpdesk emails, use the helpdesk attachment endpoint
      if (!isInsurance && ticket) {
        response = await fetchWithAuth(
          `${API_URL}/email/helpdesk-attachment/${email.messageId}/${attachment.sharePointFileId}`,
          { method: "GET" },
          session,
        );
      }
      // For insurance emails, use the insurance attachment endpoint
      else if (isInsurance) {
        response = await fetchWithAuth(
          `${API_URL}/email/insurance-attachment/${email.messageId}/${attachment.sharePointFileId}`,
          { method: "GET" },
          session,
        );
      }
      // For other emails (acquisitions), try SharePoint URL first, then fallback to attachment endpoint
      else {
        if (
          attachment.sharePointFileUrl &&
          !attachment.sharePointFileUrl.startsWith("#")
        ) {
          response = await fetch(attachment.sharePointFileUrl);
        } else {
          response = await fetchWithAuth(
            `${API_URL}/email/attachment/${email.messageId}/${attachment.sharePointFileId}`,
            { method: "GET" },
            session,
          );
        }
      }

      if (!response.ok) throw new Error("Download failed");

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.style.display = "none";
      a.href = url;
      a.download = attachment.name;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error("Error downloading attachment:", error);
      toast.error("Fout bij downloaden van bijlage");
    }
  };

  const handleQuickReplySuccess = () => {
    setShowQuickReply(false);
    fetchEmails();
    onEmailsRead?.(); // Notify parent to refresh ticket data and email counts
  };

  const handleNewEmail = () => {
    setReplyToSubject("");
    setShowQuickReply(true);
  };

  const handleSearchContacts = async (query: string) => {
    setContactSearch(query);
    if (query.length < 2) {
      setContactOptions([]);
      return;
    }

    try {
      const response = await fetchWithAuth(
        `${API_URL}/contact/search?query=${encodeURIComponent(query)}`,
        { method: "GET" },
        session,
      );
      if (response.ok) {
        const contacts = await response.json();
        setContactOptions(
          contacts.filter(
            (c: any) => c.email && !localContacts.some((lc) => lc.id === c.id),
          ),
        );
      }
    } catch (error) {
      console.error("Error searching contacts:", error);
    }
  };

  const saveContacts = async (contacts: typeof localContacts) => {
    if (!isInsurance || !insuranceIssueId) return;

    setIsUpdatingContacts(true);
    try {
      const response = await fetchWithAuth(
        `${API_URL}/insurance/file/${insuranceIssueId}/contacts`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ contactIds: contacts.map((c) => c.id) }),
        },
        session,
      );

      if (!response.ok) throw new Error("Failed to update contacts");

      setLocalContacts(contacts);
      onUpdateInsuranceContacts?.(contacts);
      setContactSearch("");
      setContactOptions([]);
      toast.success("Contacten bijgewerkt");
    } catch (error) {
      console.error("Error updating contacts:", error);
      toast.error("Fout bij bijwerken contacten");
    } finally {
      setIsUpdatingContacts(false);
    }
  };

  // Action Notes functions
  const fetchActionNotes = async () => {
    if (!ticket?.id || isInsurance) return;

    setIsLoadingNotes(true);
    try {
      const response = await fetchWithAuth(
        `${API_URL}/helpdesk/ticket/${ticket.id}/action-notes`,
        { method: "GET" },
        session,
      );

      if (response.ok) {
        const notes = await response.json();
        setActionNotes(notes);
      }
    } catch (error) {
      console.error("Error fetching action notes:", error);
    } finally {
      setIsLoadingNotes(false);
    }
  };

  const markTicketAsAnalyzed = async () => {
    if (
      !ticket?.id ||
      ticket.internalStatus !== TicketInternalStatus.NEW_UNANSWERED
    )
      return;

    try {
      const response = await fetchWithAuth(
        `${API_URL}/helpdesk/ticket/${ticket.id}`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ internalStatus: "Geanalyseerd" }),
        },
        session,
      );

      if (response.ok) {
        onTicketUpdated?.();
        console.log("Ticket automatically marked as analyzed");
      }
    } catch (error) {
      console.error("Failed to auto-mark ticket as analyzed:", error);
    }
  };

  const handleAddNote = async () => {
    if (!newMessage.trim()) {
      toast.error("Voer een bericht in");
      return;
    }

    setIsSubmitting(true);
    try {
      const noteData = {
        message: newMessage.trim(),
        remindMeAt: newRemindMeAt
          ? new Date(newRemindMeAt).toISOString()
          : null,
        userId: user?.id,
      };

      const response = await fetchWithAuth(
        `${API_URL}/helpdesk/ticket/${ticket.id}/action-notes`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(noteData),
        },
        session,
      );

      if (response.ok) {
        toast.success("Actie notitie toegevoegd");
        setNewMessage("");
        setNewRemindMeAt("");
        setShowAddNote(false);
        fetchActionNotes(); // Refresh the list
      } else {
        throw new Error("Failed to add action note");
      }
    } catch (error) {
      console.error("Error adding action note:", error);
      toast.error("Error bij toevoegen van actie notitie");
    } finally {
      setIsSubmitting(false);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInHours = (now.getTime() - date.getTime()) / (1000 * 60 * 60);

    if (diffInHours < 24) {
      return `Vandaag om ${date.toLocaleTimeString("nl-NL", { hour: "2-digit", minute: "2-digit" })}`;
    } else if (diffInHours < 48) {
      return `Gisteren om ${date.toLocaleTimeString("nl-NL", { hour: "2-digit", minute: "2-digit" })}`;
    } else if (diffInHours < 168) {
      // 7 days
      const dayNames = [
        "zondag",
        "maandag",
        "dinsdag",
        "woensdag",
        "donderdag",
        "vrijdag",
        "zaterdag",
      ];
      return `${dayNames[date.getDay()]} om ${date.toLocaleTimeString("nl-NL", { hour: "2-digit", minute: "2-digit" })}`;
    } else {
      return date.toLocaleDateString("nl-NL", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    }
  };

  const getUserImageUrlById = (userId: string) => {
    return `https://img.clerk.com/${userId}`;
  };

  const getUserName = (userId: string) => {
    // This is a simplified version - in a real app you might want to fetch user details
    if (userId === user?.id) {
      return `${user.firstName || ""} ${user.lastName || ""}`.trim() || "Jij";
    }
    return "Gebruiker";
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  // State to track loaded inline images
  const [inlineImageCache, setInlineImageCache] = useState<Map<string, string>>(
    new Map(),
  );
  const inlineImageCacheRef = useRef<Map<string, string>>(new Map());

  // Function to load inline images with authentication and return data URLs
  const loadInlineImageWithAuth = async (
    emailType: string,
    messageId: string,
    contentId: string,
  ): Promise<string> => {
    const cacheKey = `${emailType}-${messageId}-${contentId}`;

    // Check if already cached
    if (inlineImageCache.has(cacheKey)) {
      return inlineImageCache.get(cacheKey)!;
    }

    try {
      const encodedContentId = encodeURIComponent(contentId);
      const response = await fetchWithAuth(
        `${API_URL}/email/inline-attachment/${emailType}/${messageId}/${encodedContentId}`,
        { method: "GET" },
        session,
      );

      if (!response.ok) {
        throw new Error(`Failed to load inline image: ${response.status}`);
      }

      const blob = await response.blob();
      const dataUrl = URL.createObjectURL(blob);

      // Cache the result in both state and ref
      setInlineImageCache((prev) => {
        const newMap = new Map(prev).set(cacheKey, dataUrl);
        inlineImageCacheRef.current = newMap;
        return newMap;
      });

      console.log(`✅ Loaded inline image with auth: ${contentId}`);
      return dataUrl;
    } catch (error) {
      console.error(`❌ Failed to load inline image: ${contentId}`, error);
      // Return a placeholder or empty data URL
      return "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAiIGhlaWdodD0iMjAiIHZpZXdCb3g9IjAgMCAyMCAyMCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHJlY3Qgd2lkdGg9IjIwIiBoZWlnaHQ9IjIwIiBmaWxsPSIjRjNGNEY2Ii8+CjxwYXRoIGQ9Ik0xMCA2TDEwIDEwTDE0IDEwTDE0IDZIMTBaIiBmaWxsPSIjOUI5QjlCIi8+CjxwYXRoIGQ9Ik02IDEwTDYgMTRMMTAgMTRMMTAgMTBINloiIGZpbGw9IiM5QjlCOUIiLz4KPC9zdmc+";
    }
  };

  // Function to replace cid: URLs in email body with authenticated data URLs
  const replaceCidUrls = async (
    htmlContent: string,
    messageId: string,
  ): Promise<string> => {
    if (!htmlContent) return htmlContent;

    // Determine email type based on context
    let emailType = "acquisitions";
    if (isInsurance) {
      emailType = "insurance";
    } else if (ticket && !isInsurance) {
      emailType = "helpdesk";
    }

    let processedContent = htmlContent;
    const cidMatches: Array<{
      match: string;
      contentId: string;
      type: "src" | "background" | "style";
    }> = [];

    // Find all CID references
    const cidSrcPattern = /src=["']cid:([^"']+)["']/gi;
    let match;
    while ((match = cidSrcPattern.exec(htmlContent)) !== null) {
      cidMatches.push({ match: match[0], contentId: match[1], type: "src" });
    }

    const cidBgPattern = /background-image:\s*url\(["']?cid:([^"')]+)["']?\)/gi;
    while ((match = cidBgPattern.exec(htmlContent)) !== null) {
      cidMatches.push({
        match: match[0],
        contentId: match[1],
        type: "background",
      });
    }

    const cidStyleBgPattern =
      /style=["']([^"']*background-image:\s*url\(["']?cid:([^"')]+)["']?\)[^"']*)["']/gi;
    while ((match = cidStyleBgPattern.exec(htmlContent)) !== null) {
      cidMatches.push({ match: match[0], contentId: match[2], type: "style" });
    }

    // Load all images and replace URLs
    for (const cidMatch of cidMatches) {
      try {
        const dataUrl = await loadInlineImageWithAuth(
          emailType,
          messageId,
          cidMatch.contentId,
        );

        switch (cidMatch.type) {
          case "src":
            processedContent = processedContent.replace(
              cidMatch.match,
              `src="${dataUrl}"`,
            );
            console.log(
              `🔄 Replaced CID src: cid:${cidMatch.contentId} -> data URL`,
            );
            break;
          case "background":
            processedContent = processedContent.replace(
              cidMatch.match,
              `background-image: url("${dataUrl}")`,
            );
            console.log(
              `🔄 Replaced CID background: cid:${cidMatch.contentId} -> data URL`,
            );
            break;
          case "style":
            const styleMatch = cidMatch.match.match(/style=["']([^"']*)/);
            if (styleMatch) {
              const updatedStyle = styleMatch[1].replace(
                /url\(["']?cid:[^"')]+["']?\)/,
                `url("${dataUrl}")`,
              );
              processedContent = processedContent.replace(
                cidMatch.match,
                `style="${updatedStyle}"`,
              );
              console.log(
                `🔄 Replaced CID in style: cid:${cidMatch.contentId} -> data URL`,
              );
            }
            break;
        }
      } catch (error) {
        console.error(`❌ Failed to replace CID: ${cidMatch.contentId}`, error);
      }
    }

    return processedContent;
  };

  // Function to process email body and cache the result
  const processEmailBody = async (email: Email) => {
    const cacheKey = email.messageId;

    // Skip if already processed or currently processing
    if (
      processedEmailBodies.has(cacheKey) ||
      loadingInlineImages.has(cacheKey)
    ) {
      return;
    }

    setLoadingInlineImages((prev) => new Set(prev).add(cacheKey));

    try {
      const processedBody = await replaceCidUrls(email.body, email.messageId);
      setProcessedEmailBodies((prev) =>
        new Map(prev).set(cacheKey, processedBody),
      );
    } catch (error) {
      console.error("Failed to process email body:", error);
      // Fallback to original body
      setProcessedEmailBodies((prev) =>
        new Map(prev).set(cacheKey, email.body),
      );
    } finally {
      setLoadingInlineImages((prev) => {
        const newSet = new Set(prev);
        newSet.delete(cacheKey);
        return newSet;
      });
    }
  };

  // Get processed email body or return original if not processed yet
  const getEmailBody = (email: Email): string => {
    const processed = processedEmailBodies.get(email.messageId);
    return processed || email.body;
  };

  // Helpers for inline viewing
  const getFileExtension = (filename?: string) => {
    if (!filename) return "";
    const parts = filename.toLowerCase().split(".");
    return parts.length > 1 ? (parts.pop() as string) : "";
  };

  const isImageExtension = (ext: string) =>
    /^(png|jpg|jpeg|gif|webp|svg|bmp|tiff?)$/i.test(ext);

  const getLabelColor = (label: string) => {
    // Simple color mapping for labels
    const colors = {
      Elektriciteit: "border-yellow-400 bg-yellow-50 text-yellow-700",
      Water: "border-blue-400 bg-blue-50 text-blue-700",
      Verwarming: "border-red-400 bg-red-50 text-red-700",
      default: "border-neutral-400 bg-neutral-50 text-neutral-700",
    };
    return colors[label as keyof typeof colors] || colors.default;
  };

  const isImageAttachment = (attachment: EmailAttachment): boolean => {
    const byType = Boolean(
      attachment.contentType && attachment.contentType.startsWith("image/"),
    );
    const byExt = isImageExtension(getFileExtension(attachment.name));
    return byType || byExt;
  };

  const isPdfAttachment = (attachment: EmailAttachment): boolean => {
    return Boolean(
      attachment.contentType === "application/pdf" ||
        attachment.name.toLowerCase().endsWith(".pdf"),
    );
  };

  const handleEmailRowClick = (email: Email) => {
    setSelectedEmail(email);
  };

  // Utility function to open file in SharePoint/new tab
  const openFileInSharePoint = (attachment: EmailAttachment) => {
    console.log("🔗 openFileInSharePoint called for:", attachment.name, {
      sharePointFileUrl: attachment.sharePointFileUrl,
      contentType: attachment.contentType,
    });

    let fileUrl = "";

    // Try SharePoint URL first
    if (
      attachment.sharePointFileUrl &&
      !attachment.sharePointFileUrl.startsWith("#")
    ) {
      fileUrl = attachment.sharePointFileUrl;
      console.log("✅ Opening SharePoint URL:", fileUrl);
    } else {
      // Fallback to download URL (will download instead of view)
      console.log("❌ No SharePoint URL available, falling back to download");
      handleDownloadAttachment(attachment, selectedEmail!);
      return;
    }

    // Open in new tab
    try {
      window.open(fileUrl, "_blank", "noopener,noreferrer");
      console.log("🚀 window.open called successfully");
    } catch (error) {
      console.error("❌ Failed to open window:", error);
    }
  };

  // Check if file is viewable type
  const isViewableFile = (contentType: string, filename: string) => {
    const extension = filename.toLowerCase().split(".").pop() || "";
    const viewableTypes = [
      "image/jpeg",
      "image/jpg",
      "image/png",
      "image/gif",
      "image/webp",
      "image/svg+xml",
      "application/pdf",
      "video/mp4",
      "video/webm",
      "video/ogg",
      "audio/mp3",
      "audio/wav",
      "audio/ogg",
      "text/plain",
      "text/csv",
      "application/vnd.ms-excel",
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      "application/vnd.ms-powerpoint",
      "application/vnd.openxmlformats-officedocument.presentationml.presentation",
      "application/msword",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    ];

    const viewableExtensions = [
      "jpg",
      "jpeg",
      "png",
      "gif",
      "webp",
      "svg",
      "pdf",
      "mp4",
      "webm",
      "txt",
      "csv",
      "xlsx",
      "xls",
      "pptx",
      "ppt",
      "docx",
      "doc",
    ];

    return (
      viewableTypes.includes(contentType.toLowerCase()) ||
      viewableExtensions.includes(extension)
    );
  };

  // Simple Attachment Viewer Component with self-contained image loading
  const SimpleAttachmentViewer: React.FC<{
    attachment: EmailAttachment;
    email: Email;
    isImage: boolean;
    isPdf: boolean;
    handleDownloadAttachment: (
      attachment: EmailAttachment,
      email: Email,
    ) => Promise<void>;
  }> = ({ attachment, email, isImage, isPdf, handleDownloadAttachment }) => {
    const [showFullImage, setShowFullImage] = useState(false);
    const [imageUrl, setImageUrl] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [hasError, setHasError] = useState(false);
    const [hasStartedLoading, setHasStartedLoading] = useState(false);

    const attachmentKey = `${email.messageId}-${attachment.name}`;

    // Simple image loading function
    const loadImageUrl = async (): Promise<string> => {
      console.log(
        "🖼️ Loading image for:",
        attachment.name,
        "Key:",
        attachmentKey,
      );

      let response: Response;
      if (!isInsurance && ticket) {
        response = await fetchWithAuth(
          `${API_URL}/email/helpdesk-attachment/${email.messageId}/${attachment.sharePointFileId}`,
          { method: "GET" },
          session,
        );
      } else if (isInsurance) {
        response = await fetchWithAuth(
          `${API_URL}/email/insurance-attachment/${email.messageId}/${attachment.sharePointFileId}`,
          { method: "GET" },
          session,
        );
      } else {
        response = await fetchWithAuth(
          `${API_URL}/email/attachment/${email.messageId}/${attachment.sharePointFileId}`,
          { method: "GET" },
          session,
        );
      }

      if (!response.ok) {
        throw new Error(`Failed to load image: ${response.status}`);
      }

      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      console.log(
        "✅ Image loaded successfully:",
        attachment.name,
        "URL:",
        url,
      );
      return url;
    };

    // Auto-load images when component mounts
    useEffect(() => {
      if (isImage && !hasStartedLoading && !imageUrl && !hasError) {
        setHasStartedLoading(true);
        setIsLoading(true);
        setHasError(false);

        loadImageUrl()
          .then((url) => {
            setImageUrl(url);
            setHasError(false);
          })
          .catch((error) => {
            console.error("❌ Failed to load image:", attachment.name, error);
            setHasError(true);
          })
          .finally(() => {
            setIsLoading(false);
          });
      }
    }, [isImage, hasStartedLoading, imageUrl, hasError]);

    // Retry function
    const retryLoad = () => {
      if (isLoading) return;

      console.log("🔄 Retrying image load:", attachment.name);
      setIsLoading(true);
      setHasError(false);

      // Clean up old URL if exists
      if (imageUrl && imageUrl.startsWith("blob:")) {
        URL.revokeObjectURL(imageUrl);
        setImageUrl(null);
      }

      loadImageUrl()
        .then((url) => {
          setImageUrl(url);
          setHasError(false);
        })
        .catch((error) => {
          console.error(
            "❌ Failed to retry image load:",
            attachment.name,
            error,
          );
          setHasError(true);
        })
        .finally(() => {
          setIsLoading(false);
        });
    };

    // Cleanup on unmount
    useEffect(() => {
      return () => {
        if (imageUrl && imageUrl.startsWith("blob:")) {
          URL.revokeObjectURL(imageUrl);
        }
      };
    }, [imageUrl]);

    return (
      <div className="border-neutral-primary/20 relative isolate z-80 overflow-visible rounded-lg border p-4">
        <div className="mb-3 flex items-center justify-between">
          <div>
            <div className="text-neutral-primary font-medium">
              {attachment.name}
            </div>
            <div className="text-neutral-secondary-mid text-sm">
              {formatFileSize(attachment.size)} • {attachment.contentType}
            </div>
          </div>
          <div className="relative isolate z-90 flex flex-wrap items-center gap-2 overflow-visible">
            {/* View in SharePoint/Browser (for viewable files) */}
            {isViewableFile(attachment.contentType, attachment.name) && (
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  console.log(
                    "🔍 CLICK EVENT FIRED - Opening file in SharePoint:",
                    attachment.name,
                  );
                  openFileInSharePoint(attachment);
                }}
                className="flex cursor-pointer items-center gap-2 rounded-lg border-2 border-[#EA8162] bg-[#EA8162] px-3 py-2 text-sm font-semibold text-white transition-colors hover:border-[#d97153] hover:bg-[#d97153]"
              >
                <Eye size={16} />
                {isPdf
                  ? "Bekijk PDF"
                  : isImage
                    ? "Bekijk afbeelding"
                    : "Bekijk bestand"}
              </button>
            )}

            {/* Load image inline (only for images) */}
            {isImage && (
              <>
                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    if (imageUrl) {
                      setShowFullImage(true);
                    } else if (!isLoading && hasError) {
                      retryLoad();
                    }
                  }}
                  disabled={isLoading}
                  className="flex cursor-pointer items-center gap-2 rounded-lg border-2 border-[#31403B] bg-[#F9F0EE] px-3 py-2 text-sm font-semibold text-[#31403B] transition-colors hover:bg-[#efd8d2] disabled:cursor-not-allowed disabled:bg-[#F9F0EE]/70 disabled:text-[#31403B]/60"
                >
                  {isLoading ? (
                    <>
                      <div className="h-4 w-4 animate-spin rounded-full border-2 border-blue-600 border-t-transparent"></div>
                      Laden...
                    </>
                  ) : (
                    <>
                      <Image size={16} />
                      {imageUrl
                        ? "Volledig scherm"
                        : hasError
                          ? "Opnieuw laden"
                          : "Laden..."}
                    </>
                  )}
                </button>
                {hasError && !isLoading && (
                  <button
                    type="button"
                    onClick={retryLoad}
                    className="flex items-center gap-2 rounded-lg border border-[#EA8162]/30 bg-[#EA8162]/10 px-3 py-2 text-sm font-medium text-[#EA8162] transition-colors hover:bg-[#EA8162]/20"
                  >
                    <ArrowClockwise size={16} />
                    Opnieuw
                  </button>
                )}
              </>
            )}

            {/* Download button */}
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                console.log(
                  "📥 DOWNLOAD BUTTON CLICK EVENT FIRED:",
                  attachment.name,
                );
                handleDownloadAttachment(attachment, email);
              }}
              className="flex cursor-pointer items-center gap-2 rounded-lg border-2 border-[#5B7304] bg-[#5B7304] px-3 py-2 text-sm font-semibold text-white transition-colors hover:border-[#4c6103] hover:bg-[#4c6103]"
            >
              <Download size={16} />
              Download
            </button>
          </div>
        </div>

        {/* Image Preview */}
        {isImage && (
          <div className="mt-3">
            {isLoading ? (
              <div className="bg-neutral-primary/5 flex items-center justify-center rounded-sm border py-8">
                <Spinner size="md" color="primary" />
                <span className="text-neutral-secondary-mid ml-2">
                  Afbeelding laden...
                </span>
              </div>
            ) : hasError ? (
              <div className="rounded-sm border border-red-200 bg-red-50 p-4">
                <div className="mb-2 flex items-center gap-2 text-red-600">
                  <X size={16} />
                  <span className="font-medium">
                    Fout bij laden van afbeelding
                  </span>
                </div>
                <p className="text-sm text-red-500">
                  De afbeelding kon niet worden geladen. Probeer het opnieuw of
                  download het bestand.
                </p>
              </div>
            ) : imageUrl ? (
              <div className="relative">
                <div className="bg-neutral-primary/5 rounded-sm border p-2">
                  <img
                    src={imageUrl}
                    alt={attachment.name}
                    className="mx-auto block h-auto max-h-96 max-w-full cursor-pointer rounded-sm transition-opacity hover:opacity-90"
                    onClick={() => setShowFullImage(true)}
                    onLoad={() => {
                      console.log(
                        "✅ Image successfully displayed:",
                        attachment.name,
                      );
                    }}
                    onError={(e) => {
                      console.error(
                        "❌ Image display error for:",
                        attachment.name,
                        e,
                      );
                      setHasError(true);
                    }}
                  />
                  <div className="mt-2 text-center">
                    <span className="text-neutral-secondary-mid text-xs">
                      Klik op de afbeelding om volledig scherm te bekijken
                    </span>
                  </div>
                </div>

                {/* Fullscreen Modal */}
                {showFullImage && (
                  <div
                    className="bg-opacity-90 fixed inset-0 z-999 flex items-center justify-center bg-black"
                    onClick={() => setShowFullImage(false)}
                  >
                    <div className="relative max-h-[95vh] max-w-[95vw] p-4">
                      <img
                        src={imageUrl}
                        alt={attachment.name}
                        className="max-h-full max-w-full rounded-sm object-contain"
                        onClick={(e) => e.stopPropagation()}
                      />
                      <div className="absolute top-4 right-4 flex gap-2">
                        <button
                          type="button"
                          onClick={() => {
                            console.log(
                              "📥 Fullscreen download clicked:",
                              attachment.name,
                            );
                            handleDownloadAttachment(attachment, email);
                          }}
                          className="rounded-lg bg-white/20 p-2 text-white transition-colors hover:bg-white/30"
                          title="Download afbeelding"
                        >
                          <Download size={20} />
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            console.log("❌ Closing fullscreen image");
                            setShowFullImage(false);
                          }}
                          className="rounded-lg bg-white/20 p-2 text-white transition-colors hover:bg-white/30"
                          title="Sluiten"
                        >
                          <X size={20} />
                        </button>
                      </div>
                      <div className="absolute bottom-4 left-1/2 -translate-x-1/2 transform rounded-sm bg-black/50 px-3 py-1 text-sm text-white">
                        {attachment.name}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="bg-neutral-primary/5 border-neutral-primary/20 flex items-center justify-center rounded-sm border border-dashed py-6">
                <div className="text-center">
                  <div className="text-neutral-secondary-mid mb-2">🖼️</div>
                  <p className="text-neutral-secondary-mid text-sm">
                    Afbeelding wordt automatisch geladen...
                  </p>
                </div>
              </div>
            )}
          </div>
        )}

        {/* File Type Info */}
        {!isImage && (
          <div className="bg-neutral-primary/5 border-neutral-primary/20 text-neutral-primary mt-3 rounded-sm border p-3 text-sm">
            {isPdf ? (
              <>
                📄 PDF bestand - Klik op "Bekijk PDF" om te openen of "Download"
                om op te slaan
              </>
            ) : (
              <>
                📎 {attachment.contentType} -{" "}
                {isViewableFile(attachment.contentType, attachment.name)
                  ? 'Klik op "Bekijk bestand" om te openen of "Download" om op te slaan'
                  : 'Klik op "Download" om op te slaan'}
              </>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        size="5xl"
        scrollBehavior="inside"
        classNames={{
          base: "max-h-[90vh] relative z-70",
          body: "p-0 relative z-70",
        }}
      >
        <ModalContent>
          <ModalHeader className="border-neutral-primary/10 flex items-center justify-between border-b bg-white p-6">
            <div className="flex items-center gap-3">
              <div className="bg-brick-primary h-8 w-3 rounded-full"></div>
              <div className="flex flex-col">
                <div className="flex items-center gap-3">
                  <h2 className="text-neutral-primary text-xl font-semibold">
                    {isInsurance ? `INS${ticket.id}` : `#${ticket.id}`} •{" "}
                    {ticket.title}
                    {!isInsurance && communicationMode === "supplier" && (
                      <span className="text-neutral-secondary-mid ml-2 text-sm font-normal">
                        (Leverancierscommunicatie)
                      </span>
                    )}
                  </h2>
                  {ticket.label && ticket.label !== "Onbekend" && (
                    <div
                      className={`flex items-center gap-1.5 rounded-lg border-2 px-3 py-1.5 text-xs font-semibold ${getLabelColor(ticket.label)}`}
                    >
                      <Tag className="h-4 w-4" />
                      <span>{ticket.label}</span>
                    </div>
                  )}
                </div>
                <p className="text-neutral-secondary-mid mt-1 text-sm">
                  Email geschiedenis
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              {isInsurance && (
                <div className="flex items-center gap-2">
                  <span className="text-xs text-red-600">
                    Enkel interne contacten toevoegen
                  </span>
                  <div className="border-neutral-primary/20 flex items-center gap-1 rounded-full border bg-white px-2 py-1">
                    <input
                      value={contactSearch}
                      onChange={(e) => handleSearchContacts(e.target.value)}
                      placeholder="Zoek contact (min. 2 letters)"
                      className="px-1 text-sm outline-hidden"
                    />
                    <Button
                      size="sm"
                      variant="flat"
                      className="bg-moss-primary/10 text-moss-primary"
                      isLoading={isUpdatingContacts}
                      onClick={() => saveContacts(localContacts)}
                    >
                      Opslaan
                    </Button>
                  </div>
                </div>
              )}

              <Button
                onClick={handleNewEmail}
                className="bg-moss-primary hover:bg-moss-primary/90 text-white"
                startContent={<Plus size={20} />}
                isDisabled={
                  communicationMode === "supplier" && !ticket.supplier
                }
              >
                {communicationMode === "supplier"
                  ? "Email naar leverancier"
                  : "Verstuur nieuwe email"}
              </Button>

              <Button
                onClick={onClose}
                isIconOnly
                variant="light"
                className="text-neutral-secondary-mid hover:text-brick-primary"
              >
                <X size={22} />
              </Button>
            </div>
          </ModalHeader>

          <ModalBody className="p-6">
            {/* Communication Mode Display (only for helpdesk tickets with supplier) */}
            {!isInsurance && ticket?.supplier && communicationMode && (
              <div className="mb-4">
                <div className="mb-2 flex items-center gap-2">
                  <span className="text-neutral-primary text-sm font-medium">
                    Communicatie modus:
                  </span>
                  <div className="border-neutral-primary/20 bg-moss-primary/5 flex items-center gap-2 rounded-lg border px-3 py-1">
                    {communicationMode === "supplier" ? (
                      <>
                        <UserGear size={14} className="text-neutral-primary" />
                        <span className="text-neutral-primary text-sm font-medium">
                          Leverancier
                        </span>
                      </>
                    ) : (
                      <>
                        <User size={14} className="text-neutral-primary" />
                        <span className="text-neutral-primary text-sm font-medium">
                          Huurder
                        </span>
                      </>
                    )}
                  </div>
                </div>
                {communicationMode === "supplier" && (
                  <div className="text-neutral-secondary-mid bg-brick-secondary-fine rounded-sm p-2 text-xs">
                    <strong>Leverancier communicatie:</strong> Emails worden
                    verzonden via repairs@yally.be naar{" "}
                    {(ticket.supplier as any)?.email || "geen email"}
                  </div>
                )}
              </div>
            )}

            {/* Show warning if no supplier in supplier mode */}
            {!isInsurance &&
              communicationMode === "supplier" &&
              !ticket.supplier && (
                <div className="mb-4 rounded-lg border border-yellow-200 bg-yellow-50 p-3">
                  <div className="flex items-center gap-2 text-yellow-800">
                    <UserGear className="h-5 w-5" />
                    <span className="font-medium">
                      Geen leverancier geselecteerd
                    </span>
                  </div>
                  <p className="mt-1 text-sm text-yellow-700">
                    Selecteer eerst een leverancier voor dit ticket om emails te
                    kunnen versturen. Ga naar de ticket details om een
                    leverancier toe te voegen.
                  </p>
                </div>
              )}

            {/* Tabs for emails and action notes */}
            {!isInsurance && (
              <div className="mb-4">
                <div className="border-neutral-primary/20 flex border-b">
                  <button
                    onClick={() => setActiveTab("emails")}
                    className={`border-b-2 px-4 py-2 text-sm font-medium transition-colors ${
                      activeTab === "emails"
                        ? "border-moss-primary text-moss-primary"
                        : "text-neutral-secondary-mid hover:text-neutral-primary border-transparent"
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <Envelope size={16} />
                      Emails ({emails.length})
                    </div>
                  </button>
                  <button
                    onClick={() => setActiveTab("notes")}
                    className={`border-b-2 px-4 py-2 text-sm font-medium transition-colors ${
                      activeTab === "notes"
                        ? "border-moss-primary text-moss-primary"
                        : "text-neutral-secondary-mid hover:text-neutral-primary border-transparent"
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <ChatCircle size={16} />
                      Notities ({actionNotes.length})
                    </div>
                  </button>
                </div>
              </div>
            )}

            {/* Email Tab Content */}
            {(isInsurance || activeTab === "emails") && (
              <>
                {isInsurance && (
                  <div className="mb-4">
                    <div className="text-neutral-secondary-mid mb-1 text-xs">
                      Geselecteerde contacten
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {localContacts.map((c) => (
                        <div
                          key={c.id}
                          className="border-moss-primary text-moss-primary flex items-center gap-1 rounded-full border px-2 py-1 text-xs"
                        >
                          <span>
                            {c.firstName} {c.lastName}
                          </span>
                          <span className="text-neutral-secondary-mid">
                            ({c.email})
                          </span>
                          <button
                            className="ml-1 text-red-600"
                            onClick={() =>
                              saveContacts(
                                localContacts.filter((x) => x.id !== c.id),
                              )
                            }
                          >
                            ×
                          </button>
                        </div>
                      ))}
                      {contactOptions.length > 0 && (
                        <div className="w-full">
                          <div className="text-neutral-secondary-mid mb-1 text-xs">
                            Zoekresultaten
                          </div>
                          <div className="border-neutral-primary/10 max-h-32 overflow-y-auto rounded-sm border">
                            {contactOptions.map((opt) => (
                              <button
                                key={opt.id}
                                className="hover:bg-moss-primary/10 w-full px-2 py-1 text-left text-sm"
                                onClick={() =>
                                  saveContacts([
                                    ...localContacts.filter(
                                      (x) => x.id !== opt.id,
                                    ),
                                    {
                                      id: opt.id,
                                      firstName: opt.firstName,
                                      lastName: opt.lastName,
                                      email: opt.email,
                                    },
                                  ])
                                }
                              >
                                {opt.firstName} {opt.lastName}{" "}
                                <span className="text-neutral-secondary-mid">
                                  ({opt.email})
                                </span>
                              </button>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {isLoading ? (
                  <div className="flex flex-col items-center justify-center py-12">
                    <Spinner size="lg" color="primary" />
                    <p className="text-neutral-secondary-mid mt-4">
                      Emails laden...
                    </p>
                  </div>
                ) : emails.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-12">
                    <Envelope className="text-neutral-secondary-mid/50 mb-4 h-16 w-16" />
                    <p className="text-neutral-primary mb-2 text-lg font-medium">
                      Geen emails gevonden
                    </p>
                    <p className="text-neutral-secondary-mid mb-6 text-sm">
                      Er zijn nog geen emails gekoppeld aan dit{" "}
                      {isInsurance ? "dossier" : "ticket"}.
                    </p>
                    <Button
                      onClick={handleNewEmail}
                      className="bg-brick-primary hover:bg-brick-primary/90 text-white"
                      startContent={<Plus className="h-4 w-4" />}
                    >
                      Verstuur eerste email
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-6">
                    {/* Email Flow Table */}
                    <div className="border-neutral-primary/20 rounded-lg border bg-white">
                      <div className="border-neutral-primary/10 border-b p-4">
                        <h3 className="text-neutral-primary font-semibold">
                          Email verloop ({emails.length})
                        </h3>
                        <p className="text-neutral-secondary-mid text-sm">
                          Klik op een rij om de volledige email en bijlagen te
                          bekijken
                        </p>
                      </div>

                      <Table
                        aria-label="Email flow table"
                        classNames={{
                          wrapper: `shadow-none rounded-none ${disableHoverOverlays ? "pointer-events-none md:pointer-events-auto" : ""}`,
                          th: "bg-neutral-primary/5 text-neutral-primary font-medium",
                          td: "py-3",
                        }}
                      >
                        <TableHeader>
                          <TableColumn>RICHTING</TableColumn>
                          <TableColumn>DATUM/TIJD</TableColumn>
                          <TableColumn>VAN</TableColumn>
                          <TableColumn>NAAR</TableColumn>
                          <TableColumn>ONDERWERP</TableColumn>
                          <TableColumn>STATUS</TableColumn>
                          <TableColumn>BIJLAGEN</TableColumn>
                        </TableHeader>
                        <TableBody>
                          {emails
                            .sort(
                              (a, b) =>
                                new Date(b.receivedDateTime).getTime() -
                                new Date(a.receivedDateTime).getTime(),
                            )
                            .map((email) => {
                              const isSent = email.direction === "sent";
                              const isRecent = isRecentIncoming(email);

                              return (
                                <TableRow
                                  key={email.id}
                                  className={`hover:bg-neutral-primary/5 group cursor-pointer ${
                                    selectedEmail &&
                                    selectedEmail.id === email.id
                                      ? "bg-moss-primary/10 border-moss-primary border-l-4"
                                      : ""
                                  }`}
                                  onClick={() => handleEmailRowClick(email)}
                                >
                                  <TableCell>
                                    <Tooltip
                                      isDisabled={disableHoverOverlays}
                                      content={
                                        <div className="max-w-md p-3">
                                          <div className="mb-2 font-semibold">
                                            {email.subject}
                                          </div>
                                          <div className="text-neutral-secondary-mid mb-2 text-sm">
                                            Van: {email.from} •{" "}
                                            {new Date(
                                              email.receivedDateTime,
                                            ).toLocaleString("nl-NL")}
                                          </div>
                                          <div className="prose prose-sm text-neutral-primary max-h-64 max-w-none overflow-y-auto whitespace-pre-wrap">
                                            {getEmailBody(email)}
                                          </div>
                                          {loadingInlineImages.has(
                                            email.messageId,
                                          ) && (
                                            <div className="text-neutral-secondary-mid mt-1 flex items-center gap-1 text-xs">
                                              <Spinner size="sm" />
                                              Afbeeldingen laden...
                                            </div>
                                          )}
                                        </div>
                                      }
                                      placement="right"
                                      delay={disableHoverOverlays ? 0 : 500}
                                      classNames={{
                                        content:
                                          "bg-white border border-neutral-primary/20 shadow-lg max-w-lg",
                                      }}
                                    >
                                      <div className="flex items-center gap-2">
                                        {isSent ? (
                                          <ArrowRight
                                            className="text-moss-primary"
                                            size={16}
                                          />
                                        ) : (
                                          <ArrowLeft
                                            className="text-blue-600"
                                            size={16}
                                          />
                                        )}
                                        <Chip
                                          size="sm"
                                          className={
                                            isSent
                                              ? "bg-moss-primary/10 text-moss-primary"
                                              : "bg-blue-100 text-blue-700"
                                          }
                                        >
                                          {isSent ? "Verzonden" : "Ontvangen"}
                                        </Chip>
                                      </div>
                                    </Tooltip>
                                  </TableCell>
                                  <TableCell>
                                    <div className="text-sm">
                                      <div className="font-medium">
                                        {new Date(
                                          email.receivedDateTime,
                                        ).toLocaleDateString("nl-NL")}
                                      </div>
                                      <div className="text-neutral-secondary-mid">
                                        {new Date(
                                          email.receivedDateTime,
                                        ).toLocaleTimeString("nl-NL", {
                                          hour: "2-digit",
                                          minute: "2-digit",
                                        })}
                                      </div>
                                    </div>
                                  </TableCell>
                                  <TableCell>
                                    <div className="max-w-32 truncate text-sm font-medium">
                                      {email.from}
                                    </div>
                                  </TableCell>
                                  <TableCell>
                                    <div className="max-w-32 truncate text-sm font-medium">
                                      {email.metadata?.to || "-"}
                                    </div>
                                  </TableCell>
                                  <TableCell>
                                    <div className="max-w-48 truncate text-sm font-medium">
                                      {email.subject}
                                    </div>
                                  </TableCell>
                                  <TableCell>
                                    <div className="flex items-center gap-2">
                                      {!email.isRead && !isSent && (
                                        <Chip
                                          size="sm"
                                          className="bg-red-100 text-red-700"
                                        >
                                          Ongelezen
                                        </Chip>
                                      )}
                                      {isRecent && (
                                        <Chip
                                          size="sm"
                                          className="bg-orange-100 text-orange-700"
                                        >
                                          Nieuw
                                        </Chip>
                                      )}
                                      {email.isRead && !isSent && (
                                        <Chip
                                          size="sm"
                                          className="bg-green-100 text-green-700"
                                        >
                                          Gelezen
                                        </Chip>
                                      )}
                                    </div>
                                  </TableCell>
                                  <TableCell>
                                    {email.hasAttachments && (
                                      <Tooltip
                                        isDisabled={disableHoverOverlays}
                                        onOpenChange={(isOpen) => {
                                          if (
                                            isOpen &&
                                            email.hasAttachments &&
                                            (!email.attachments ||
                                              email.attachments.length === 0)
                                          ) {
                                            fetchEmailAttachments(email);
                                          }
                                        }}
                                        content={
                                          <div className="max-w-sm p-3">
                                            <div className="mb-2 font-semibold">
                                              Bijlagen (
                                              {isLoadingAttachments.has(
                                                email.messageId,
                                              )
                                                ? "..."
                                                : (email.attachments?.length ??
                                                  "?")}
                                              )
                                            </div>
                                            {isLoadingAttachments.has(
                                              email.messageId,
                                            ) ? (
                                              <div className="text-neutral-secondary-mid flex items-center gap-2 text-sm">
                                                <Spinner size="sm" />
                                                Bijlagen laden...
                                              </div>
                                            ) : email.attachments &&
                                              email.attachments.length > 0 ? (
                                              <div className="space-y-2">
                                                {email.attachments.map(
                                                  (attachment, index) => (
                                                    <div
                                                      key={`${attachment.name}-${index}`}
                                                      className="bg-neutral-primary/5 hover:bg-neutral-primary/10 flex cursor-pointer items-center justify-between gap-2 rounded-sm p-2"
                                                      onClick={() =>
                                                        handleDownloadAttachment(
                                                          attachment,
                                                          email,
                                                        )
                                                      }
                                                    >
                                                      <div className="min-w-0 flex-1">
                                                        <div className="truncate text-sm font-medium">
                                                          {attachment.name}
                                                        </div>
                                                        <div className="text-neutral-secondary-mid text-xs">
                                                          {formatFileSize(
                                                            attachment.size,
                                                          )}
                                                        </div>
                                                      </div>
                                                      <Download
                                                        size={14}
                                                        className="text-moss-primary shrink-0"
                                                      />
                                                    </div>
                                                  ),
                                                )}
                                              </div>
                                            ) : (
                                              <div className="text-neutral-secondary-mid text-sm">
                                                {email.hasAttachments
                                                  ? "Klik om bijlagen te laden"
                                                  : "Geen bijlagen"}
                                              </div>
                                            )}
                                          </div>
                                        }
                                        placement="left"
                                        delay={disableHoverOverlays ? 0 : 300}
                                        classNames={{
                                          content:
                                            "bg-white border border-neutral-primary/20 shadow-lg",
                                        }}
                                      >
                                        <div className="flex items-center gap-1">
                                          <Paperclip
                                            size={14}
                                            className="text-neutral-secondary-mid"
                                          />
                                          <span className="text-neutral-secondary-mid text-sm">
                                            {isLoadingAttachments.has(
                                              email.messageId,
                                            )
                                              ? "..."
                                              : (email.attachments?.length ??
                                                "?")}
                                          </span>
                                          <Button
                                            size="sm"
                                            variant="flat"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              if (
                                                !email.attachments ||
                                                email.attachments.length === 0
                                              ) {
                                                fetchEmailAttachments(email);
                                              }
                                            }}
                                            className="ml-1 opacity-0 transition-opacity group-hover:opacity-100"
                                            isIconOnly
                                            isLoading={isLoadingAttachments.has(
                                              email.messageId,
                                            )}
                                          >
                                            {email.attachments &&
                                            email.attachments.length > 0 ? (
                                              <Download size={12} />
                                            ) : (
                                              <Paperclip size={12} />
                                            )}
                                          </Button>
                                        </div>
                                      </Tooltip>
                                    )}
                                  </TableCell>
                                </TableRow>
                              );
                            })}
                        </TableBody>
                      </Table>
                    </div>

                    {/* Expanded Email View */}
                    {selectedEmail && (
                      <div className="border-neutral-primary/20 mt-6 rounded-lg border bg-white">
                        <div className="border-neutral-primary/10 flex items-center justify-between border-b p-4">
                          <h3 className="text-neutral-primary font-semibold">
                            Email Details
                          </h3>
                          <Button
                            onClick={() => setSelectedEmail(null)}
                            isIconOnly
                            variant="light"
                            className="text-neutral-secondary-mid hover:text-brick-primary"
                            size="sm"
                          >
                            <X size={18} />
                          </Button>
                        </div>

                        <div className="p-4">
                          {/* Email Header */}
                          <div className="border-neutral-primary/10 mb-4 border-b pb-4">
                            <h4 className="text-neutral-primary mb-2 text-lg font-semibold">
                              {selectedEmail.subject}
                            </h4>
                            <div className="text-neutral-secondary-mid space-y-1 text-sm">
                              <div>
                                <strong>Van:</strong> {selectedEmail.from}
                              </div>
                              {selectedEmail.metadata?.to && (
                                <div>
                                  <strong>Naar:</strong>{" "}
                                  {selectedEmail.metadata.to}
                                </div>
                              )}
                              <div>
                                <strong>Datum:</strong>{" "}
                                {new Date(
                                  selectedEmail.receivedDateTime,
                                ).toLocaleString("nl-NL")}
                              </div>
                              <div className="flex items-center gap-2">
                                <strong>Richting:</strong>
                                <Chip
                                  size="sm"
                                  className={
                                    selectedEmail.direction === "sent"
                                      ? "bg-moss-primary/10 text-moss-primary"
                                      : "bg-blue-100 text-blue-700"
                                  }
                                >
                                  {selectedEmail.direction === "sent"
                                    ? "Verzonden"
                                    : "Ontvangen"}
                                </Chip>
                              </div>
                            </div>
                          </div>

                          {/* Email Body */}
                          <div className="mb-6">
                            <h5 className="text-neutral-primary mb-3 font-medium">
                              Bericht
                            </h5>
                            <div
                              className="prose prose-sm text-neutral-primary bg-neutral-primary/5 max-h-96 max-w-none overflow-y-auto rounded-sm border p-4 select-text"
                              dangerouslySetInnerHTML={{
                                __html: getEmailBody(selectedEmail),
                              }}
                              style={{ pointerEvents: "auto" }}
                            />
                            {loadingInlineImages.has(
                              selectedEmail.messageId,
                            ) && (
                              <div className="text-neutral-secondary-mid mt-2 flex items-center gap-2 text-sm">
                                <Spinner size="sm" />
                                Embedded afbeeldingen worden geladen...
                              </div>
                            )}
                          </div>

                          {/* Attachments */}
                          {selectedEmail.hasAttachments && (
                            <div>
                              <div className="mb-3 flex items-center justify-between">
                                <h5 className="text-neutral-primary font-medium">
                                  Bijlagen{" "}
                                  {selectedEmail.attachments
                                    ? `(${selectedEmail.attachments.length})`
                                    : ""}
                                </h5>
                                {!selectedEmail.attachments && (
                                  <Button
                                    size="sm"
                                    variant="flat"
                                    onClick={() =>
                                      fetchEmailAttachments(selectedEmail)
                                    }
                                    isLoading={isLoadingAttachments.has(
                                      selectedEmail.messageId,
                                    )}
                                    className="bg-moss-primary/10 text-moss-primary hover:bg-moss-primary/20"
                                    startContent={<Paperclip size={16} />}
                                  >
                                    Bijlagen laden
                                  </Button>
                                )}
                              </div>
                              {selectedEmail.attachments &&
                                selectedEmail.attachments.length > 0 && (
                                  <div className="space-y-4">
                                    {selectedEmail.attachments.map(
                                      (attachment, index) => (
                                        <SimpleAttachmentViewer
                                          key={`${attachment.name}-${index}`}
                                          attachment={attachment}
                                          email={selectedEmail}
                                          isImage={isImageAttachment(
                                            attachment,
                                          )}
                                          isPdf={isPdfAttachment(attachment)}
                                          handleDownloadAttachment={
                                            handleDownloadAttachment
                                          }
                                        />
                                      ),
                                    )}
                                  </div>
                                )}
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </>
            )}

            {/* Action Notes Tab Content */}
            {!isInsurance && activeTab === "notes" && (
              <div className="space-y-4">
                {/* Add New Note Button */}
                <div className="flex items-center justify-between">
                  <h3 className="text-neutral-primary font-semibold">
                    Actie Notities
                  </h3>
                  <Button
                    onClick={() => setShowAddNote(true)}
                    size="sm"
                    className="bg-moss-primary hover:bg-moss-primary/90 text-white"
                    startContent={<Plus size={16} />}
                  >
                    Notitie toevoegen
                  </Button>
                </div>

                {/* Add Note Form */}
                {showAddNote && (
                  <div className="bg-neutral-primary/5 border-neutral-primary/20 rounded-lg border p-4">
                    <div className="space-y-3">
                      <Textarea
                        label="Bericht"
                        placeholder="Voer uw actie notitie in..."
                        value={newMessage}
                        onChange={(e) => setNewMessage(e.target.value)}
                        minRows={3}
                        classNames={{
                          input: "bg-white",
                          inputWrapper: "border border-neutral-primary/20",
                        }}
                      />

                      <Input
                        type="datetime-local"
                        label="Herinner me op (optioneel)"
                        value={newRemindMeAt}
                        onChange={(e) => setNewRemindMeAt(e.target.value)}
                        classNames={{
                          input: "bg-white",
                          inputWrapper: "border border-neutral-primary/20",
                        }}
                      />

                      <div className="flex justify-end gap-2">
                        <Button
                          variant="light"
                          onClick={() => {
                            setShowAddNote(false);
                            setNewMessage("");
                            setNewRemindMeAt("");
                          }}
                          disabled={isSubmitting}
                        >
                          Annuleren
                        </Button>
                        <Button
                          onClick={handleAddNote}
                          isLoading={isSubmitting}
                          className="bg-moss-primary hover:bg-moss-primary/90 text-white"
                        >
                          Toevoegen
                        </Button>
                      </div>
                    </div>
                  </div>
                )}

                {/* Notes List */}
                {isLoadingNotes ? (
                  <div className="flex items-center justify-center py-8">
                    <Spinner size="lg" />
                    <span className="text-neutral-secondary-mid ml-2">
                      Notities laden...
                    </span>
                  </div>
                ) : actionNotes.length === 0 ? (
                  <div className="text-neutral-secondary-mid py-8 text-center">
                    <ChatCircle size={48} className="mx-auto mb-2 opacity-50" />
                    <p>Geen actie notities gevonden</p>
                    <p className="text-sm">
                      Klik op "Notitie toevoegen" om de eerste toe te voegen
                    </p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {actionNotes.map((note) => (
                      <div
                        key={note.id}
                        className="border-neutral-primary/20 rounded-lg border bg-white p-4"
                      >
                        <div className="flex items-start gap-3">
                          <img
                            src={getUserImageUrlById(note.userId)}
                            alt={getUserName(note.userId)}
                            className="h-8 w-8 rounded-full"
                            onError={(e) => {
                              const target = e.target as HTMLImageElement;
                              target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(getUserName(note.userId))}&background=31403B&color=F9F0EE`;
                            }}
                          />
                          <div className="min-w-0 flex-1">
                            <div className="mb-1 flex items-center gap-2">
                              <span className="text-neutral-primary text-sm font-medium">
                                {getUserName(note.userId)}
                              </span>
                              <span className="text-neutral-secondary-mid text-xs">
                                {formatDate(note.createdAt)}
                              </span>
                            </div>
                            <div className="text-neutral-primary text-sm whitespace-pre-wrap">
                              {note.message}
                            </div>
                            {note.remindMeAt && (
                              <div className="text-brick-primary bg-brick-secondary-fine mt-2 flex inline-flex items-center gap-1 rounded-sm px-2 py-1 text-xs">
                                <Clock size={12} />
                                Herinnering: {formatDate(note.remindMeAt)}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </ModalBody>
        </ModalContent>
      </Modal>

      {/* Quick Reply Modal */}
      {showQuickReply && (
        <QuickReplyModal
          isOpen={showQuickReply}
          onClose={() => setShowQuickReply(false)}
          onSuccess={handleQuickReplySuccess}
          ticket={ticket}
          replyToSubject={replyToSubject}
          isInsurance={isInsurance}
          insuranceIssueId={insuranceIssueId}
          insuranceRecipients={
            isInsurance
              ? localContacts
                  .filter((c) => !!c.email)
                  .map((c) => c.email as string)
              : undefined
          }
          communicationMode={communicationMode}
          hasExistingEmails={emails.length > 0}
        />
      )}
    </>
  );
};
